import { useCallback, useEffect, useState, useRef, useContext } from 'react'
import { useIntl } from 'react-intl'

import TripNameField from './TripDetailsFields/TripNameField'

import MessageBanner from 'LuxKit/Banners/MessageBanner'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import { ModalElementContext } from 'contexts/ModalProvider'
import type { UpdateTripError } from 'tripPlanner/api/types'
import { useUpdateTrip } from 'tripPlanner/hooks/api/trip'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'
import type { BasicTrip, FullTrip } from 'tripPlanner/types/common'

interface Props {
  trip: BasicTrip | FullTrip
}

function EditTripName(props: Props) {
  const { trip } = props
  const inputRef = useRef<HTMLInputElement>(null)
  const intl = useIntl()

  const renameTripTitle = intl.formatMessage({
    id: 'AnHUrOoLIMfjfmYGJvGG',
    defaultMessage: 'Rename your trip',
    description: 'Title for the rename trip modal in the trip planner feature',
  })

  const saveLabel = intl.formatMessage({
    id: 'ZsvzOvIGZDIibzDUzbNN',
    defaultMessage: 'Save',
    description: 'Save button label in the rename trip modal in the trip planner',
  })

  const cancelLabel = intl.formatMessage({
    id: 'sbfrXJ5SJRIA5ru4wk_O',
    defaultMessage: 'Cancel',
    description: 'Cancel button label in the rename trip modal in the trip planner',
  })

  const { closeModal, goBack } = useModalCallbacks()
  const modalElement = useContext(ModalElementContext)

  const onCloseModalClicked = useCallback(() => {
    closeModal()
    modalElement?.resolve()
  }, [closeModal, modalElement])
  const [name, setName] = useState(trip.name)
  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined)

  const { mutate, isLoading } = useUpdateTrip({
    onSuccess: () => {
      onCloseModalClicked()
    },
    onError: (e: UpdateTripError) => {
      if (e.status === 422) {
        setErrorMsg(e.message)
      }
    },
  })

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.select()
    }
  }, [])

  const onUpdate = useCallback(() => {
    if (name.length) {
      mutate({
        tripId: trip.id,
        name,
      })
    }
  }, [mutate, name, trip.id])

  return (
    <>
      <ModalHeader
        title={renameTripTitle}
        onBackButtonClick={goBack}
        onCloseButtonClick={onCloseModalClicked}
      />
      <ModalBody>
        <ModalContent>
          <TripNameField
            value={name}
            onChange={setName}
            autoSelect
            disabled={isLoading}
            data-testid="trip-popup-trip-input"
          />
          {errorMsg && <MessageBanner kind="critical">{errorMsg}</MessageBanner>}
        </ModalContent>
      </ModalBody>
      <ModalFooter
        primaryActionProps={{
          children: saveLabel,
          disabled: isLoading || name.length == 0,
          onClick: onUpdate,
        }}
        secondaryActionProps={{
          children: cancelLabel,
          disabled: isLoading,
          onClick: onCloseModalClicked,
        }}
      />
    </>
  )
}

export default EditTripName
